import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner, Alert, Badge, Table, Row, Col, Card } from 'react-bootstrap';
import { FaBox, FaBoxOpen, FaWarehouse, FaWeight } from 'react-icons/fa';
import axios from 'axios';
import config from '../../config';
import './StockBreakdownModal.css';

const StockBreakdownModal = ({ show, onHide, item, productId }) => {
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [error, setError] = useState(null);
  
    const fetchStockBreakdown = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/inventory/stock-breakdown/${productId}`,
          { withCredentials: true }
        );
        setStockData(response.data.data); // Access nested data property
      } catch (err) {
        console.error('Error fetching stock breakdown:', err);
        setError(err.response?.data?.message || 'Failed to fetch stock breakdown');
      } finally {
        setLoading(false);
      }
    },[productId]);

  useEffect(() => {
    if (show && productId) {
      fetchStockBreakdown();
    }
  }, [show, productId, fetchStockBreakdown]);

  const calculateTotalPacked = () => {
    return stockData?.summary?.totalPackedQuantity || 0;
  };

  const calculateTotalUnpacked = () => {
    return stockData?.summary?.totalUnpackedQuantity || 0;
  };

  const calculateTotalBags = () => {
    return stockData?.summary?.totalBags || 0;
  };

  const calculateTotalAvailable = () => {
    return stockData?.summary?.totalAvailable || 0;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="stock-breakdown-header">
        <Modal.Title>
          <FaWarehouse className="me-2" />
          Stock Breakdown - {item?.product?.productName || 'Product'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading stock details...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : stockData ? (
          <>
            {/* Summary Cards */}
            <Row className="mb-4">
              <Col xl={3} md={6} className="mb-3">
                <Card className="stock-summary-card total-stock">
                  <Card.Body className="d-flex align-items-center">
                    <div className="card-icon me-3">
                      <FaWarehouse />
                    </div>
                    <div className="card-content">
                      <h6>Total Available</h6>
                      <h3>{calculateTotalAvailable().toFixed(2)}</h3>
                      <small className="text-muted">kg</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={3} md={6} className="mb-3">
                <Card className="stock-summary-card packed-stock">
                  <Card.Body className="d-flex align-items-center">
                    <div className="card-icon me-3">
                      <FaBox />
                    </div>
                    <div className="card-content">
                      <h6>Packed Stock</h6>
                      <h3>{calculateTotalPacked().toFixed(2)}</h3>
                      <small className="text-muted">kg</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={3} md={6} className="mb-3">
                <Card className="stock-summary-card unpacked-stock">
                  <Card.Body className="d-flex align-items-center">
                    <div className="card-icon me-3">
                      <FaBoxOpen />
                    </div>
                    <div className="card-content">
                      <h6>Loose/Unpacked</h6>
                      <h3>{calculateTotalUnpacked().toFixed(2)}</h3>
                      <small className="text-muted">kg</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={3} md={6} className="mb-3">
                <Card className="stock-summary-card bags-count">
                  <Card.Body className="d-flex align-items-center">
                    <div className="card-icon me-3">
                      <FaBox />
                    </div>
                    <div className="card-content">
                      <h6>Total Bags</h6>
                      <h3>{calculateTotalBags()}</h3>
                      <small className="text-muted">bags</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Packed Stock Details */}
            {stockData.packedStock && stockData.packedStock.length > 0 && (
              <div className="mb-4">
                <h5 className="section-title">
                  <FaBox className="me-2" />
                  Packed Stock Details
                </h5>
                <Table striped bordered hover responsive className="stock-breakdown-table">
                  <thead>
                    <tr>
                      <th>Packing Material</th>
                      <th>Warehouse</th>
                      <th>Total IN</th>
                      <th>Total OUT</th>
                      <th>Available Qty</th>
                      <th>Bags IN</th>
                      <th>Bags OUT</th>
                      <th>Available Bags</th>
                      <th>Qty/Bag</th>
                      <th>Outer Packing Material</th>
                      <th>Outer Bags</th>
                      <th>Bags/Outer Bag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockData.packedStock.map((pack, index) => {
                      const packingMaterial = pack.packingMaterial;
                      const quantityPerBag = pack.quantityPerBag || 0;

                      return (
                        <tr key={index}>
                          <td>
                            <div>
                              <Badge bg="info" className="me-2">
                                {packingMaterial?.packingMaterialName || 'N/A'}
                              </Badge>
                              <br />
                              <small className="text-muted">
                                {packingMaterial?.packingSize || ''}
                              </small>
                            </div>
                            {pack.outerPackingMaterial && (
                              <div className="mt-2">
                                <Badge bg="secondary" className="me-2">
                                  Outer: {pack.outerPackingMaterial?.packingMaterialName || 'N/A'}
                                </Badge>
                                <br />
                                <small className="text-muted">
                                  {pack.outerPackingMaterial?.packingSize || ''}
                                </small>
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge bg="secondary">
                              <FaWarehouse className="me-1" />
                              {pack.warehouse?.name || 'N/A'}
                            </Badge>
                          </td>
                          <td className="text-end text-success">
                            <strong>{pack.totalIn.toFixed(2)}</strong>
                            <small className="d-block text-muted">{pack.unit}</small>
                          </td>
                          <td className="text-end text-danger">
                            <strong>{pack.totalOut.toFixed(2)}</strong>
                            <small className="d-block text-muted">{pack.unit}</small>
                          </td>
                          <td className="text-end">
                            <FaWeight className="me-1 text-muted" />
                            <strong className="text-primary">{pack.availableQuantity.toFixed(2)}</strong>
                            <small className="d-block text-muted">{pack.unit}</small>
                          </td>
                          <td className="text-center text-success">
                            <Badge bg="success" style={{ fontSize: '0.9rem', padding: '0.4rem 0.6rem' }}>
                              +{pack.totalBagsIn || 0}
                            </Badge>
                          </td>
                          <td className="text-center text-danger">
                            <Badge bg="danger" style={{ fontSize: '0.9rem', padding: '0.4rem 0.6rem' }}>
                              -{pack.totalBagsOut || 0}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="primary" style={{ fontSize: '1rem', padding: '0.5rem 0.75rem' }}>
                              {pack.availableBags || 0}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <strong className="text-primary">
                              {quantityPerBag.toFixed(2)}
                            </strong>
                          </td>
                          <td>
                            {pack.outerPackingMaterial ? (
                              <Badge bg="info" className="me-2">
                                {pack.outerPackingMaterial?.packingMaterialName || 'N/A'}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-center">
                            {pack.outerAvailableBags !== undefined ? (
                              <Badge bg="secondary" style={{ fontSize: '1rem', padding: '0.5rem 0.75rem' }}>
                                {pack.outerAvailableBags || 0}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-center">
                            {pack.bagsPerOuterBag !== undefined ? (
                              <strong className="text-info">
                                {pack.bagsPerOuterBag.toFixed(2)}
                              </strong>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="table-info fw-bold">
                      <td colSpan="7" className="text-end">Total Packed:</td>
                      <td className="text-end text-primary">{calculateTotalPacked().toFixed(2)} kg</td>
                      <td colSpan="3" className="text-center">Total Bags:</td>
                      <td className="text-center">
                        <Badge bg="dark" style={{ fontSize: '1rem', padding: '0.5rem 0.75rem' }}>
                          {calculateTotalBags()}
                        </Badge>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}

            {/* Unpacked Stock Details */}
            {stockData.unpackedStock && stockData.unpackedStock.length > 0 && (
              <div>
                <h5 className="section-title">
                  <FaBoxOpen className="me-2" />
                  Loose/Unpacked Stock
                </h5>
                <Table striped bordered hover responsive className="stock-breakdown-table">
                  <thead>
                    <tr>
                      <th>Warehouse</th>
                      <th>Total IN</th>
                      <th>Total OUT</th>
                      <th>Available Quantity</th>
                      <th>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockData.unpackedStock.map((stock, index) => (
                      <tr key={index}>
                        <td>
                          <Badge bg="secondary">
                            <FaWarehouse className="me-1" />
                            {stock.warehouse?.name || 'N/A'}
                          </Badge>
                        </td>
                        <td className="text-end text-success">
                          <strong>{stock.totalIn.toFixed(2)}</strong>
                        </td>
                        <td className="text-end text-danger">
                          <strong>{stock.totalOut.toFixed(2)}</strong>
                        </td>
                        <td className="text-end">
                          <FaWeight className="me-1 text-muted" />
                          <strong className="text-warning">{stock.availableQuantity.toFixed(2)}</strong>
                        </td>
                        <td className="text-center">
                          <Badge bg="light" text="dark">{stock.unit}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-warning fw-bold">
                      <td className="text-end">Total Unpacked:</td>
                      <td colSpan="2"></td>
                      <td className="text-end text-warning">{calculateTotalUnpacked().toFixed(2)} kg</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
                <Alert variant="warning" className="d-flex align-items-center mt-3">
                  <FaBoxOpen size={40} className="me-3" />
                  <div>
                    <strong>{calculateTotalUnpacked().toFixed(2)} kg</strong> of loose/unpacked stock is available across {stockData.unpackedStock.length} warehouse location(s).
                    <br />
                    <small className="text-muted">
                      This stock needs to be packed before dispatch.
                    </small>
                  </div>
                </Alert>
              </div>
            )}

            {/* No Unpacked Stock Message */}
            {(!stockData.unpackedStock || stockData.unpackedStock.length === 0) && calculateTotalPacked() > 0 && (
              <Alert variant="success" className="d-flex align-items-center">
                <FaBox size={40} className="me-3" />
                <div>
                  <strong>All stock is packed!</strong>
                  <br />
                  <small className="text-muted">
                    All available stock for this product is properly packed and ready for dispatch.
                  </small>
                </div>
              </Alert>
            )}

            {/* No Stock Message */}
            {calculateTotalPacked() === 0 && calculateTotalUnpacked() === 0 && (
              <Alert variant="info" className="text-center py-5">
                <FaBoxOpen size={60} className="mb-3 text-muted" />
                <h5>No Stock Available</h5>
                <p className="mb-0 text-muted">There is no stock available for this product.</p>
              </Alert>
            )}
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default StockBreakdownModal;
